import { Box, ButtonGroup } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import { Outlet, useLocation, useNavigate, useParams } from "@tanstack/react-router"
import type { FC } from "react"

import { usePurchaseCostDialogStore } from "./components/PurchaseCosts/usePurchaseCostDialogStore"
import { usePurchaseCostsPermissions } from "./hooks/usePurchaseCostsPermissions"
import { ProductCostTabPathname } from "./types"

import { Button } from "@/components/common/Button"

const TAB_CLASSES = "rounded-none border-gray-400 uppercase text-gray-700"

export const PRODUCT_COSTS_LAYOUT_ROUTE_PATH = "/app/product/$productId/edit/_layout/costs/_layout"

export const getProductCostsTabRoute = (
  productId: string,
  tab: ProductCostTabPathname = ProductCostTabPathname.LaidDown
): string => `/app/product/${productId}/edit/costs/${tab}`

export const ProductCostsTabsLayout: FC = () => {
  const { productId } = useParams({ from: PRODUCT_COSTS_LAYOUT_ROUTE_PATH })
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const openFormDialog = usePurchaseCostDialogStore((state) => state.openFormDialog)
  const { canEdit } = usePurchaseCostsPermissions()

  const handleNavigateToTab = (tab: ProductCostTabPathname) => {
    navigate({ to: getProductCostsTabRoute(productId, tab) })
  }

  const isActiveTab = (tab: ProductCostTabPathname) => pathname.endsWith(`/${tab}`)

  const getActiveTabClass = (tab: ProductCostTabPathname) => (isActiveTab(tab) ? "bg-gray-300" : "")

  const handleAddVendorPricing = () => {
    openFormDialog({ warehouseAreaId: null, editingCost: null, isFuture: false })
  }

  const isPurchaseCostsActive = isActiveTab(ProductCostTabPathname.PurchaseCosts)

  return (
    <Box className='flex h-full flex-col gap-y-6'>
      <Box className='flex items-center justify-between'>
        <ButtonGroup
          color='inherit'
          orientation='horizontal'
          variant='outlined'
          className='shrink-0'
          data-testid='product-costs-button-group'
        >
          <Button
            appearance='outlined'
            className={`${TAB_CLASSES} rounded-l border-r-0 ${getActiveTabClass(ProductCostTabPathname.LaidDown)}`}
            onClick={() => handleNavigateToTab(ProductCostTabPathname.LaidDown)}
          >
            LAID DOWN COSTS
          </Button>
          <Button
            appearance='outlined'
            className={`${TAB_CLASSES} rounded-r ${getActiveTabClass(ProductCostTabPathname.PurchaseCosts)}`}
            onClick={() => handleNavigateToTab(ProductCostTabPathname.PurchaseCosts)}
          >
            PURCHASE COSTS
          </Button>
        </ButtonGroup>
        {isPurchaseCostsActive && canEdit && (
          <Button
            appearance='outlined'
            startIcon={<AddOutlined />}
            onClick={handleAddVendorPricing}
            data-testid='product.purchase-costs.add-vendor-pricing-button'
          >
            Vendor Pricing
          </Button>
        )}
      </Box>
      <Box className='flex-1 overflow-auto'>
        <Outlet />
      </Box>
    </Box>
  )
}
